/**
 * Shared "WB button" primitives.
 *
 * The website builder has SEVERAL places that emit clickable button elements
 * inside the canvas (premium ext renderers, legacy hero, StyleButtons helper,
 * FullBlock extras zone). To make them all uniformly:
 *   • detectable by useInlineButtonEditor (`data-wb-button="1"` + index)
 *   • drag-reorderable by useSectionSortable (`draggable` + `wb-sortable-item`)
 *   • write to the right content field (`data-wb-button-field`)
 *
 * we expose ONE helper + ONE component. Use the component for new code; use
 * the helper to spread attrs into existing JSX shapes (legacy <span>/<a>)
 * without rewriting them.
 */
import React from 'react';

/**
 * Returns the standard data-attrs + draggable + className for any element
 * that should behave as an editable, drag-reorderable WB button.
 *
 * Spread this onto your existing <a>/<span> JSX:
 *   <a {...getWbButtonAttrs(i)} className={"my-base " + getWbButtonAttrs(i).className}>...
 *
 * Or use the WbButton component below for new code.
 */
export function getWbButtonAttrs(index, field = 'buttons') {
  // Iter 160 — REMOVED HTML5 `draggable` attribute. It was conflicting
  // with the mouse-based useFluidTextDrag free-position system: the
  // browser's native HTML5 drag would steal the mousedown, fire its
  // own dragstart/drop cycle, and the button would visually snap back
  // to its origin on release because no CSS transform was ever applied.
  //
  // Buttons are now free-positioned via the Fluid grid (translate-based)
  // just like headings, paragraphs and images. Sibling-reorder happens
  // inside the section's Edit Drawer (Squarespace pattern).
  return {
    'data-wb-button': '1',
    'data-wb-button-index': index,
    'data-wb-button-field': field,
    className: 'wb-sortable-item',
  };
}

/**
 * Drop-in <WbButton/> — renders an anchor with the WB button affordances.
 * Pass `field="_extras_buttons"` for buttons inside the extras zone.
 */
export const WbButton = React.forwardRef(function WbButton(
  { text, link, buttonIndex, field = 'buttons', color, className = '', style = {}, children, ...rest },
  ref
) {
  const attrs = getWbButtonAttrs(buttonIndex, field);
  return (
    <a
      ref={ref}
      href={link || '#'}
      {...attrs}
      className={`${attrs.className} ${className}`.trim()}
      style={style}
      {...rest}
    >
      {children ?? text ?? 'Button'}
    </a>
  );
});
